/**
 * Every user-visible string, in Polish — the customer's decision of 2026-08-11:
 * Polish UI, English code. Keeping them in one typed module means the UI cannot
 * drift into mixed languages and a future second language is one file away.
 */

type PrivacySection = { heading: string; paragraphs?: string[]; bullets?: string[] };

/** Polish plural: 1 → `one`; 2–4, 22–24, … (not 12–14) → `few`; else `many`. */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const isFew = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return isFew ? few : many;
}

/** "1 wolne miejsce", "3 wolne miejsca", "5 wolnych miejsc". */
function seatsLabel(n: number): string {
  return `${n} ${plural(n, "wolne miejsce", "wolne miejsca", "wolnych miejsc")}`;
}

export const t = {
  appName: "Dojazdy Skiba",
  tagline: "Wspólne dojazdy do pracy — ZM SKIBA S.A.",

  common: {
    loading: "Wczytywanie…",
    save: "Zapisz",
    cancel: "Anuluj",
    close: "Zamknij",
    confirm: "Potwierdź",
    back: "Wróć",
    details: "Szczegóły",
    genericError: "Coś poszło nie tak. Spróbuj ponownie.",
    loadError: "Nie udało się wczytać danych. Sprawdź połączenie i spróbuj ponownie.",
    notFoundTitle: "Nie ma takiej strony",
    notFoundBody: "Sprawdź adres albo wróć na stronę główną.",
    goHome: "Strona główna",
  },

  auth: {
    signInTitle: "Zaloguj się",
    signInDescription: "Wpisz dane otrzymane od administratora.",
    username: "Nazwa użytkownika",
    usernamePlaceholder: "np. jan.kowalski",
    password: "Hasło",
    signIn: "Zaloguj się",
    signingIn: "Logowanie…",
    noAccount: "Nie masz konta?",
    requestAccess: "Poproś o dostęp",
    inviteOnly: "Dostęp tylko dla pracowników",
    usernameIsEmail: "E-mail (login)",
    usernameEmailPlaceholder: "np. jan.kowalski@gmail.com",
    signOut: "Wyloguj się",
    forceChangeTitle: "Ustaw własne hasło",
    forceChangeBody:
      "Twoje obecne hasło nadał administrator. Zanim przejdziesz dalej, ustaw takie, które znasz tylko Ty.",
    currentPassword: "Obecne hasło (od administratora)",
    newPassword: "Nowe hasło",
    confirmPassword: "Powtórz nowe hasło",
    passwordMinLength: "Hasło musi mieć co najmniej 12 znaków.",
    passwordsDontMatch: "Hasła nie są takie same.",
    passwordSameAsCurrent: "Nowe hasło musi się różnić od obecnego.",
    passwordRule: "Co najmniej 12 znaków — najlepiej dłuższe hasło albo kilka słów.",
    setPassword: "Ustaw hasło i przejdź dalej",
    settingPassword: "Zapisywanie…",
    passwordChanged: "Hasło ustawione. Miłej jazdy!",
    forgotPassword: "Nie pamiętasz hasła?",
    forgotTitle: "Reset hasła",
    forgotDescription:
      "Podaj adres e-mail, którym się logujesz — wyślemy Ci kod do ustawienia nowego hasła. Jeśli Twój login nie jest adresem e-mail, skontaktuj się z działem IT.",
    sendCode: "Wyślij kod",
    sendingCode: "Wysyłanie…",
    haveCode: "Mam już kod",
    resetTitle: "Nowe hasło",
    resetDescription: "Wpisz kod z wiadomości e-mail i ustaw nowe hasło.",
    codeSent: "Jeśli konto istnieje, kod jest już w drodze na jego adres e-mail.",
    resetCode: "Kod z e-maila",
    resetCodePlaceholder: "np. 1234 5678",
    resetSubmit: "Ustaw nowe hasło",
    resendCode: "Poproś o nowy kod",
    backToSignIn: "Wróć do logowania",
  },

  signup: {
    title: "Poproś o dostęp",
    subtitle:
      "Wypełnij formularz — sprawdzimy, czy pracujesz w Zakładach Mięsnych Skiba S.A., i wyślemy Ci e-mail z hasłem tymczasowym.",
    fullName: "Imię i nazwisko",
    fullNamePlaceholder: "np. Jan Kowalski",
    email: "Prywatny adres e-mail",
    emailHint: "Będzie Twoim loginem do aplikacji.",
    phone: "Numer telefonu",
    phonePlaceholder: "np. 600 100 200",
    phoneHint:
      "Widzi go tylko kierowca samochodu, którym jeździsz (oraz administrator), żeby móc do Ciebie zadzwonić. Inni pasażerowie go nie widzą.",
    submit: "Wyślij zgłoszenie",
    submitting: "Wysyłanie…",
    sentTitle: "Zgłoszenie wysłane!",
    sentBody:
      "Dział IT zweryfikuje zgłoszenie i wyśle Ci e-mail z hasłem tymczasowym. Pierwsze logowanie poprosi Cię o ustawienie własnego.",
    backToLogin: "Wróć do logowania",
    // Rendered as: prefix + <link>linkText</link> + suffix.
    privacyConsentPrefix: "Zapoznałem(-am) się z ",
    privacyConsentLink: "informacją o przetwarzaniu danych osobowych",
    privacyConsentSuffix:
      " i przyjmuję do wiadomości, że mój numer telefonu będzie widoczny dla kierowcy mojego samochodu.",
  },

  footer: {
    privacy: "Ochrona danych",
    corporatePolicy: "Polityka prywatności ZM Skiba",
  },

  /**
   * RODO notice for the app. App-specific facts only (what is stored, who
   * sees the phone number, retention periods agreed 2026-08-11); legal bases
   * and the full rights catalogue defer to the controller's policy on
   * zmskiba.pl. Content to be reviewed by the customer's HR before go-live.
   */
  privacy: {
    title: "Ochrona danych osobowych",
    subtitle: "Informacja o przetwarzaniu danych w aplikacji Dojazdy Skiba",
    updated: "Wersja z 22 października 2026 r.",
    intro:
      "Ta informacja uzupełnia Politykę prywatności ZM Skiba S.A. i opisuje, co dzieje się z Twoimi danymi w aplikacji do wspólnych dojazdów do pracy. Pełna treść polityki:",
    corporatePolicy: "Polityka prywatności ZM Skiba S.A.",
    corporatePolicyUrl: "https://zmskiba.pl/polityka-prywatnosci/",
    sections: [
      {
        heading: "Administrator danych",
        paragraphs: [
          "Administratorem Twoich danych osobowych jest Zakłady Mięsne Skiba S.A. z siedzibą przy ul. Derdowskiego 23, 89-600 Chojnice.",
        ],
      },
      {
        heading: "Jakie dane przetwarzamy",
        bullets: [
          "imię i nazwisko oraz prywatny adres e-mail (jest Twoim loginem),",
          "numer telefonu,",
          "miejscowość, z której dojeżdżasz — wybieraną z listy, nigdy dokładny adres,",
          "jeśli jesteś kierowcą: model samochodu, liczbę miejsc, miejsce zbiórki, godziny wyjazdu i powrotu oraz zakład docelowy,",
          "skład Twojej załogi i historię członkostwa (zgłoszenia, od kiedy jeździsz, odejścia),",
          "wiadomości na czacie załogi oraz statusy dnia (na czas, spóźnienie, odwołany kurs),",
          "dane techniczne: identyfikatory sesji, subskrypcję powiadomień na tym urządzeniu oraz adres IP w logach serwera — wyłącznie w celach bezpieczeństwa.",
        ],
      },
      {
        heading: "W jakim celu",
        paragraphs: [
          "Dane służą wyłącznie do organizacji wspólnych dojazdów do pracy: dopasowania załóg po trasie, kontaktu wewnątrz załogi, informowania o zmianach w kursie oraz zabezpieczenia Twojego konta. Nie wykorzystujemy ich do żadnych innych celów.",
        ],
      },
      {
        heading: "Kto widzi Twoje dane",
        bullets: [
          "Numer telefonu widzi wyłącznie kierowca samochodu, którym jeździsz, oraz administratorzy — po to, żeby kierowca mógł zadzwonić w sprawie spóźnienia lub odbioru. Pasażerowie nie widzą numerów innych pasażerów ani numeru kierowcy; nie jest on też widoczny na liście ofert.",
          "Imię i nazwisko oraz miejscowość widzą pracownicy przeglądający oferty przejazdów. Dokładne miejsce zbiórki kierowcy widzi tylko jego załoga.",
          "Czat, statusy dnia i historię członkostwa widzi Twoja załoga.",
          "Administratorzy aplikacji (dział IT) mają dostęp do danych w zakresie potrzebnym do zarządzania kontami i pomocy użytkownikom.",
          "Dostawcy techniczni: serwer aplikacji w Unii Europejskiej, Brevo (wysyłka e-maili, serwery w UE) oraz — jeśli włączysz powiadomienia — usługa push Twojej przeglądarki (Google, Apple lub Mozilla), która otrzymuje treść w formie zaszyfrowanej.",
        ],
      },
      {
        heading: "Jak długo przechowujemy dane",
        bullets: [
          "wiadomości na czacie i statusy dnia — 30 dni,",
          "zakończone członkostwa w załogach — 90 dni od zakończenia, potem są anonimizowane,",
          "dane konta — przez czas korzystania z aplikacji; po dezaktywacji konta (np. po odejściu z firmy) dane są anonimizowane po 90 dniach.",
        ],
      },
      {
        heading: "Twoje prawa",
        paragraphs: [
          "Masz prawo dostępu do swoich danych, ich sprostowania (imię, telefon i miejscowość zmienisz w aplikacji), usunięcia, ograniczenia przetwarzania oraz wniesienia sprzeciwu. W tych sprawach skontaktuj się z działem IT lub napisz na skiba@zmskiba.pl. Przysługuje Ci też prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.",
          "Podstawy prawne przetwarzania oraz pełny opis Twoich praw znajdziesz w Polityce prywatności ZM Skiba S.A.",
        ],
      },
    ] as PrivacySection[],
  },

  push: {
    enable: "Włącz powiadomienia",
    disable: "Wyłącz powiadomienia",
    enabled: "Powiadomienia włączone — czat, zmiany wyjazdu i prośby o miejsce.",
    disabled: "Powiadomienia wyłączone.",
    denied: "Powiadomienia są zablokowane w ustawieniach przeglądarki.",
    installHint: "Dodaj aplikację do ekranu głównego, aby dostawać powiadomienia.",
  },

  days: {
    label: "W które dni?",
    drivingDays: "Dni jazdy",
    full: "komplet",
    everyWorkday: "pn–pt",
    // "najbliższy wyjazd w czwartek" — the preposition-and-case form, Monday-first.
    onDay: [
      "w poniedziałek",
      "we wtorek",
      "w środę",
      "w czwartek",
      "w piątek",
      "w sobotę",
      "w niedzielę",
    ],
    freeOn: (day: string, free: number) => `${day}: ${seatsLabel(free)}`,
  },

  nav: {
    home: "Start",
    admin: "Administracja",
    settings: "Ustawienia konta",
  },

  settings: {
    title: "Ustawienia konta",
    changePasswordTitle: "Zmień hasło",
    changePasswordBody:
      "Po zmianie zostaniesz wylogowany na wszystkich innych urządzeniach — tutaj zalogujemy Cię ponownie automatycznie.",
    currentPassword: "Obecne hasło",
    changePassword: "Zmień hasło",
    passwordChanged: "Hasło zmienione.",
    closeAccountTitle: "Zamknij konto",
    closeAccountBody:
      "Zamknięcie jest natychmiastowe: opuścisz swoje grupy, a jeśli jesteś kierowcą, Twoje grupy zostaną wstrzymane, a pasażerowie dostaną powiadomienie, żeby poszukać innego auta. Konto może ponownie otworzyć tylko dział IT; po 90 dniach Twoje dane osobowe i grupy są usuwane.",
    closeAccount: "Zamknij konto",
    closeAccountConfirmTitle: "Na pewno zamknąć konto?",
    closeAccountConfirmBody: "Aby potwierdzić, wpisz swoje obecne hasło.",
    closing: "Zamykanie…",
    accountClosed: "Konto zamknięte. Do zobaczenia!",
  },

  home: {
    yourCrew: "Twój przejazd",
    yourCrews: "Twoje przejazdy",
    todayIs: (date: string) => `Dziś jest ${date}`,
    browseTitle: "Wszystkie samochody",
    browseSubtitle: "Kto jeździ, skąd i o której — poproś o miejsce.",
    noGroups: "Nie ma jeszcze żadnych grup. Załóż pierwszą!",
    noOtherGroups: "Nie ma jeszcze innych samochodów.",
    noGroupsForPlant: "Do tego zakładu nie jeździ jeszcze żadne inne auto.",
    noGroupYet: "Nie należysz jeszcze do żadnego samochodu",
    noGroupYetBody:
      "Znajdź samochód, który wyjeżdża z Twojej okolicy o dobrej porze, i wyślij prośbę o dołączenie — albo załóż własną grupę, jeśli masz auto.",
    createGroup: "Załóż własną grupę",
    requestPending: (driver: string) =>
      `Czekasz na decyzję kierowcy w sprawie dołączenia: ${driver}. Damy Ci znać!`,
    withdrawRequest: "Wycofaj prośbę o dołączenie",
    requestSummary: "Twoja prośba o dołączenie",
    yourTown: "Twoja miejscowość",
    yourTownHint: "Podaj, skąd dojeżdżasz, a oznaczymy auta, które masz po drodze.",
    destinationFilter: "Dokąd jeździsz?",
    allPlants: "Wszystkie",
  },

  group: {
    driver: "Kierowca",
    departure: "Wyjazd",
    return: "Powrót",
    seatsFree: (n: number) => (n === 0 ? "komplet" : seatsLabel(n)),
    crew: (current: number, total: number) => `Załoga (${current}/${total})`,
    ridingSince: (date: string) => `jeździ od ${date}`,
    onYourRoute: "Po drodze",
    destination: "Zakład",
    join: "Chcę się zabrać",
    joinTitle: "Prośba o dołączenie",
    joinBody: (driver: string) =>
      `Prośba trafi do kierowcy (${driver}). Po akceptacji dołączysz do grupy i jej czatu.`,
    joinMessage: "Wiadomość dla kierowcy (opcjonalnie)",
    joinMessagePlaceholder: "np. Od poniedziałku, wsiadam przy rondzie",
    joinSent: "Prośba wysłana. Kierowca da Ci znać!",
    full: "Brak wolnych miejsc",
    paused: "Grupa wstrzymana",
    noFreeDaysForYou:
      "W dni, w które ten samochód ma wolne miejsca, masz już swój dojazd — własnym autem albo z inną załogą.",
    busyDaysHidden:
      "Nie pokazujemy dni, w które już jeździsz (własnym autem albo z inną załogą) ani tych bez wolnych miejsc.",
    notFound: "Nie ma takiej grupy — mogła zostać usunięta.",
    exactPickup: "Miejsce zbiórki",
    exactPickupHidden: "Dokładny adres zobaczysz po dołączeniu do grupy.",
    leave: "Opuść grupę",
    leaveConfirmTitle: "Opuścić grupę?",
    leaveConfirmBody:
      "Stracisz dostęp do czatu, a Twoje miejsce w aucie będzie wolne dla innych. Zawsze możesz poprosić o dołączenie ponownie.",
    left: "Opuściłeś grupę.",
    requestWithdrawn: "Prośba o dołączenie wycofana.",
    settings: "Ustawienia grupy",
    myDays: "Twoje dni",
    changeDays: "Zmień dni",
    changeDaysTitle: "Z których dni rezygnujesz?",
    changeDaysBody:
      "Odznacz dni, w które nie jeździsz już z tą załogą. Dni, z których rezygnujesz, możesz od razu zająć w innym samochodzie. Nowe dni wymagają osobnej prośby do kierowcy.",
    changeDaysAllRemoved: "Żeby zrezygnować ze wszystkich dni, opuść grupę.",
    changeDaysSave: "Zapisz dni",
    daysUpdated: "Dni zmienione. Te, z których rezygnujesz, możesz zająć w innym samochodzie.",
  },

  status: {
    onTime: "Jedziemy planowo",
    delayed: (min: number) => `Wyjazd ${min} min później`,
    cancelled: "Dziś nie jedziemy",
    setOnTime: "Planowo",
    setDelayed: "Później…",
    setCancelled: "Nie jedziemy",
    delayDialogTitle: "O ile minut później?",
    delayMinutes: "Minuty",
    noteLabel: "Krótka notatka (opcjonalnie)",
    notePlaceholder: "np. Korek na obwodnicy",
    updated: "Status na dziś ustawiony.",
  },

  chat: {
    title: "Czat grupy",
    placeholder: "Napisz wiadomość…",
    send: "Wyślij",
    empty: "Jeszcze nikt nic nie napisał. Przywitaj się!",
    system: "Komunikat",
    retention: "Wiadomości znikają automatycznie po 30 dniach.",
    remaining: (used: number, max: number) => `${used}/${max} znaków`,
  },

  absence: {
    title: "Nieobecności",
    report: "Nie jadę…",
    dialogTitle: "W który dzień nie jedziesz?",
    dialogBody:
      "Kierowca dostanie powiadomienie, a na czacie załogi pojawi się informacja. Zawsze możesz to odwołać.",
    dateLabel: "Data",
    submit: "Zgłoś nieobecność",
    added: "Nieobecność zgłoszona.",
    removed: "Nieobecność odwołana.",
    withdraw: "Odwołaj",
    you: "Ty",
    today: "dziś",
    tomorrow: "jutro",
  },

  driver: {
    requests: "Prośby o dołączenie",
    accept: "Przyjmij",
    decline: "Odrzuć",
    accepted: "Pasażer dołączył do grupy.",
    declined: "Prośba odrzucona.",
    removePassenger: "Usuń",
    removeConfirmTitle: (name: string) => `Usunąć: ${name}?`,
    removeConfirmBody:
      "Ta osoba straci miejsce w samochodzie i dostęp do czatu grupy.",
    removed: "Pasażer usunięty z grupy.",
    todayControls: "Status na dziś",
    pausedTitle: "Grupa wstrzymana",
    pausedBody: "Nie widać jej na liście samochodów i nikt nie może do niej dołączyć.",
    resume: "Wznów grupę",
    resumed: "Grupa wznowiona — znów widać ją na liście samochodów.",
    notToday: (nextDay?: string) =>
      nextDay
        ? `Dziś to auto nie jeździ — najbliższy wyjazd ${nextDay}.`
        : "Dziś to auto nie jeździ.",
  },

  createGroup: {
    title: "Załóż grupę",
    subtitle: "Twój samochód, Twoje zasady — pasażerowie proszą o miejsce.",
    carModel: "Samochód",
    carModelPlaceholder: "np. Škoda Octavia",
    seats: "Liczba miejsc dla pasażerów",
    area: "Skąd wyjeżdżasz?",
    areaPlaceholder: "Zacznij pisać nazwę miejscowości…",
    areaHint: "Publicznie widać tylko nazwę miejscowości — nigdy dokładny adres.",
    plant: "Do którego zakładu jedziesz?",
    plantPlaceholder: "Wybierz zakład",
    townSearchEmpty: "Nie znaleziono takiej miejscowości.",
    townSearchMin: "Wpisz co najmniej 2 litery.",
    pickupDetails: "Dokładne miejsce zbiórki",
    pickupDetailsPlaceholder: "np. ul. Polna 12, przy sklepie",
    pickupDetailsHint: "Zobaczą je tylko zaakceptowani pasażerowie.",
    departureTime: "Godzina wyjazdu",
    returnTime: "Godzina powrotu",
    submit: "Opublikuj grupę",
    created: "Grupa założona. Powodzenia!",
    daysBusyHint: (days: string) =>
      `W te dni jeździsz już w innej grupie — jako pasażer albo kierowca: ${days}. Wybierz inne dni albo najpierw zrezygnuj z nich w tamtej grupie.`,
  },

  editGroup: {
    title: "Ustawienia grupy",
    subtitle:
      "Zmiany godzin lub dni jazdy załoga zobaczy od razu — na czacie pojawi się komunikat.",
    submit: "Zapisz zmiany",
    saved: "Zmiany zapisane.",
    endTitle: "Zakończ grupę",
    endBody:
      "Auto zniknie z listy, a wszyscy pasażerowie stracą miejsce (dostaną powiadomienie). Czat i historia grupy zostaną usunięte. Będziesz mógł założyć nową grupę.",
    end: "Zakończ grupę",
    endConfirmTitle: "Zakończyć grupę?",
    endConfirmBody: (crew: number) =>
      crew === 0
        ? "Tej operacji nie można cofnąć."
        : `Miejsce straci ${crew} ${crew === 1 ? "pasażer" : "pasażerów"}. Tej operacji nie można cofnąć.`,
    ended: "Grupa zakończona.",
  },

  admin: {
    title: "Administracja",
    usersTab: "Pracownicy",
    requestsTab: "Zgłoszenia",
    areasTab: "Miejscowości",
    groupsTab: "Grupy",
    approve: "Zatwierdź",
    reject: "Odrzuć",
    approveTitle: (name: string) => `Utwórz konto: ${name}`,
    approveBody:
      "Loginem będzie adres e-mail. Aplikacja wygeneruje hasło tymczasowe i wyśle je e-mailem — przy pierwszym logowaniu ta osoba ustawi własne.",
    approvedEmailSent: "Konto utworzone. E-mail z hasłem tymczasowym został wysłany.",
    passwordFallbackTitle: "Przekaż hasło telefonicznie",
    passwordFallbackBody:
      "Konto utworzono, ale e-mail z hasłem nie został wysłany (wysyłka jest wyłączona albo wystąpił błąd). Zapisz hasło tymczasowe i przekaż je tej osobie — po zamknięciu tego okna nie zobaczysz go ponownie.",
    loginLabel: "Login",
    rejected: "Zgłoszenie odrzucone.",
    noRequests: "Brak nowych zgłoszeń.",
    requestedAt: (date: string) => `zgłoszono ${date}`,
    addUser: "Dodaj konto",
    statActive: "Aktywne konta",
    statNotLoggedIn: "Jeszcze nie zalogowani",
    statInactive: "Nieaktywne",
    statCo2: (km: number) => `kg CO₂ mniej tygodniowo (szacunek, ok. ${km} km)`,
    addUserTitle: "Nowe konto pracownika",
    username: "Login (e-mail lub nazwa)",
    usernameHint: "Może być prywatny e-mail, nazwa albo po prostu imię i nazwisko.",
    fullName: "Imię i nazwisko",
    phone: "Telefon (opcjonalnie)",
    phoneHint: "Widoczny tylko dla kierowcy samochodu i administratorów.",
    initialPassword: "Hasło początkowe",
    passwordHint: "Co najmniej 12 znaków. Przekaż je pracownikowi bezpiecznie.",
    isAdmin: "Uprawnienia administratora",
    userCreated: "Konto utworzone.",
    active: "Aktywne",
    deactivated: "Dezaktywowane",
    thatIsYou: "To Ty",
    deactivateConfirmTitle: (name: string) => `Dezaktywować konto: ${name}?`,
    deactivateConfirmBody:
      "Osoba zostanie natychmiast wylogowana i wypisana ze swoich przejazdów. Konto można później aktywować ponownie.",
    // "Ta osoba prowadzi 2 grupy — …"; accusative: 1 grupę, 2–4 grupy, 5+ grup.
    deactivateDriverNote: (groups: number) => {
      const count = `${groups} ${plural(groups, "grupę", "grupy", "grup")}`;
      const paused = groups === 1 ? "zostanie wstrzymana" : "zostaną wstrzymane";
      return `Ta osoba prowadzi ${count} — ${paused}, a pasażerowie dostaną powiadomienie, żeby poszukać innego auta.`;
    },
    deactivate: "Dezaktywuj",
    reactivate: "Aktywuj ponownie",
    userUpdated: "Konto zaktualizowane.",
    resetPassword: "Resetuj hasło",
    resetPasswordTitle: (name: string) => `Nowe hasło dla: ${name}`,
    passwordReset: "Hasło zresetowane — wszystkie sesje wylogowane.",
    addArea: "Dodaj miejscowość",
    addAreaTitle: "Nowa miejscowość",
    editAreaTitle: "Edytuj miejscowość",
    areaName: "Nazwa miejscowości",
    areaNamePlaceholder: "np. Topole",
    areaDistrict: "Powiat (opcjonalnie)",
    areaDistrictPlaceholder: "np. chojnicki",
    areaCoords: "Współrzędne (opcjonalnie)",
    areaCoordsPlaceholder: "np. 53.69292, 17.51773",
    areaCoordsHint:
      "Środek miejscowości, wklejony z map (szerokość, długość). Bez współrzędnych miejscowość nigdy nie będzie oznaczana jako „Po drodze”.",
    areaCoordsInvalid: "Nieprawidłowe współrzędne — użyj formatu 53.69292, 17.51773.",
    areaManualHint:
      "Lista zawiera wszystkie oficjalne miejscowości w Polsce (rejestr PRNG). Ręcznie dodawaj tylko te, których faktycznie brakuje.",
    areaSearchPlaceholder: "Szukaj miejscowości…",
    areaNoCoords: "brak współrzędnych",
    areaNoResults: "Nie znaleziono takiej miejscowości.",
    areaMoreResults: "Pokazano pierwsze 100 wyników — wpisz nazwę, aby zawęzić.",
    areaCreated: "Miejscowość dodana.",
    areaUpdated: "Miejscowość zaktualizowana.",
    areaActive: "Widoczna",
    noUsers: "Brak kont.",
    noAreas: "Dodaj pierwsze miejscowości — bez nich nie da się założyć grupy.",
    role: (isAdmin: boolean) => (isAdmin ? "Administrator" : "Pracownik"),
    // "Kierowca", "Kierowca · 2 grupy", "Kierowca · 5 grup"
    driverBadge: (groups: number) => {
      if (groups <= 1) return "Kierowca";
      return `Kierowca · ${groups} ${plural(groups, "grupa", "grupy", "grup")}`;
    },
    anonymised: "Dane usunięte",
    showErased: (count: number) => `Pokaż usunięte konta (${count})`,
    eraseData: "Usuń dane teraz",
    eraseConfirmTitle: (name: string) => `Usunąć dane: ${name}?`,
    eraseConfirmBody:
      "Imię i nazwisko, login, telefon i miejscowość zostaną trwale usunięte, a konto zanonimizowane. Jeśli ta osoba prowadziła grupy, one też zostaną usunięte. Bez tego dzieje się to samo 90 dni po dezaktywacji. Tej operacji nie można cofnąć.",
    erase: "Usuń dane",
    erased: "Dane osobowe usunięte.",
    groupsHint:
      "Wszystkie przejazdy, także wstrzymane. Grupy dezaktywowanych kierowców znikają same razem z ich danymi — po 90 dniach albo po „Usuń dane teraz”.",
    noGroups: "Nie ma jeszcze żadnych przejazdów.",
    groupPaused: "Wstrzymana",
    openGroup: "Otwórz",
    deleteGroup: "Usuń przejazd",
    deleteGroupConfirmTitle: (driver: string) => `Usunąć przejazd: ${driver}?`,
    deleteGroupConfirmBody:
      "Przejazd zniknie na stałe razem z czatem, statusami dnia i historią załogi. Pasażerowie dostaną powiadomienie. Tej operacji nie można cofnąć.",
    groupDeleted: "Przejazd usunięty.",
  },
} as const;
